import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Search,
  Users,
  TrendingUp,
  AlertTriangle,
  Eye,
  MessageSquare,
  Calendar,
  Filter,
} from "lucide-react";
import { collection, query, where, getDocs, onSnapshot } from "firebase/firestore";
import Header from "../Components/Header";
import { db, auth } from "../firebase/config";
import { onAuthStateChanged } from "firebase/auth";
import CoachingTrackSkeleton from "../Skeleton/CoachingTrackSkeleton";
import { Link } from "react-router-dom";

const CoachingTrack = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("Tous");
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true); // Overall loading state
  const [isGlobalRoadmapLoading, setIsGlobalRoadmapLoading] = useState(true); // New: Tracks global roadmap loading
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    averageProgress: 0,
    alerts: 0,
  });
  const globalRoadmapStructure = useRef(null);
  const [currentAdminEmail, setCurrentAdminEmail] = useState(null);
  const [filterByOrganizer, setFilterByOrganizer] = useState(false);

  // Helper to generate avatar initials
  const generateAvatar = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  // Effect to get the current logged-in admin's email
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user && user.email) {
        setCurrentAdminEmail(user.email);
      } else {
        setCurrentAdminEmail(null);
      }
    });
    return () => unsubscribeAuth();
  }, []);

  // Effect to fetch global roadmap structure once on component mount
  useEffect(() => {
    const fetchGlobalRoadmap = async () => {
      setIsGlobalRoadmapLoading(true); // Start loading global roadmap
      try {
        const roadmapLevelsSnapshot = await getDocs(
          collection(db, "roadmapLevels")
        );
        const allLevelsData = [];
        const globalRoadmapItemsFlat = [];

        for (const levelDoc of roadmapLevelsSnapshot.docs) {
          const levelData = { id: levelDoc.id, ...levelDoc.data() };
          const sessionsSnapshot = await getDocs(
            collection(db, `roadmapLevels/${levelDoc.id}/roadmap_sessions`)
          );
          const levelSessions = [];
          sessionsSnapshot.forEach((sessionDoc) => {
            const sessionData = { id: sessionDoc.id, ...sessionDoc.data(), levelId: levelDoc.id };
            levelSessions.push(sessionData);
            globalRoadmapItemsFlat.push(sessionData);
          });
          levelSessions.sort((a, b) => (a.order || 0) - (b.order || 0));
          allLevelsData.push({ ...levelData, items: levelSessions });
        }
        allLevelsData.sort((a, b) => (a.order || 0) - (b.order || 0));
        globalRoadmapStructure.current = { levels: allLevelsData, flatItems: globalRoadmapItemsFlat };
        console.log("Global roadmap structure fetched.");
      } catch (error) {
        console.error("Error fetching global roadmap structure:", error);
      } finally {
        setIsGlobalRoadmapLoading(false); // Global roadmap loading finished
      }
    };

    fetchGlobalRoadmap();
  }, []); // Empty dependency array means this runs once on mount

  // Main effect for real-time student data and progress
  useEffect(() => {
    // Only proceed if global roadmap is loaded AND currentAdminEmail is determined
    // (currentAdminEmail is needed for the organizer filter, even if not active, to prevent re-runs)
    if (isGlobalRoadmapLoading || currentAdminEmail === null) {
      setLoading(true); // Keep overall loading true if prerequisites aren't met
      return;
    }

    setLoading(true); // Set overall loading true when starting to fetch/listen for students

    const usersRef = collection(db, "users");
    let q = query(
      usersRef,
      where("role", "in", ["user", "", null]),
      where("hasBookedSession", "==", true)
    );

    // Set up onSnapshot listener for users
    const unsubscribeUsers = onSnapshot(q, async (querySnapshot) => {
      const studentsData = [];
      const globalRoadmapItemsFlat = globalRoadmapStructure.current.flatItems;

      // Use Promise.all to fetch roadmapProgress for all students concurrently
      const studentPromises = querySnapshot.docs.map(async (userDoc) => {
        const data = userDoc.data();
        const studentId = userDoc.id;

        // Fetch student's specific roadmap progress (using getDocs inside onSnapshot)
        const studentProgressSnapshot = await getDocs(
          collection(db, `users/${studentId}/roadmapProgress`)
        );
        const studentRoadmapProgress = {};
        studentProgressSnapshot.forEach((progressDoc) => {
          studentRoadmapProgress[progressDoc.id] = progressDoc.data();
        });

        let completedCount = 0;
        let inProgressCount = 0;
        let totalItemsForProgress = globalRoadmapItemsFlat.length;

        globalRoadmapItemsFlat.forEach((globalItem) => {
          const studentItem = studentRoadmapProgress[globalItem.id];
          if (studentItem) {
            if (studentItem.status === "completed") {
              completedCount++;
            } else if (studentItem.status === "in-progress") {
              inProgressCount++;
            }
          }
        });

        const studentProgress =
          totalItemsForProgress > 0
            ? Math.round((completedCount / totalItemsForProgress) * 100)
            : 0;

        const remainingCount = totalItemsForProgress - completedCount;

        return {
          id: studentId,
          name: data.name || data.displayName || "Unknown",
          email: data.email || "",
          avatar: data.avatar || null,
          profileImage: data.profilePhoto || null,
          level: data.level || "Niveau 1",
          status: data.status || "Actif",
          tags: data.tags || [],
          progress: studentProgress,
          completed: completedCount,
          remaining: remainingCount,
          bgColor: data.bgColor || "bg-blue-600",
          createdAt: data.createdAt,
          lastActive: data.lastActive,
          organizer: data.organizer || null,
          hasBookedSession: data.hasBookedSession || false,
        };
      });

      const fetchedStudents = await Promise.all(studentPromises);
      setStudents(fetchedStudents); // Store all fetched students

      const totalStudents = fetchedStudents.length;
      const activeStudents = fetchedStudents.filter(
        (s) => s.status === "Actif"
      ).length;
      const averageProgress =
        totalStudents > 0
          ? Math.round(
              fetchedStudents.reduce((acc, s) => acc + s.progress, 0) /
                totalStudents
            )
          : 0;
      const alerts = fetchedStudents.filter(
        (s) => s.progress < 30 && s.status === "Actif"
      ).length;

      setStats({
        total: totalStudents,
        active: activeStudents,
        averageProgress,
        alerts,
      });
      // --- End stats calculation ---

      setLoading(false); // Set overall loading false once student data is processed
    }, (error) => {
      console.error("Error fetching student data snapshot:", error);
      setLoading(false); // Set loading false even on error
    });

    // Cleanup function for the users listener
    return () => unsubscribeUsers();
  }, [isGlobalRoadmapLoading, currentAdminEmail]); // Depend on global roadmap loading and admin email

  // Filter students based on search term, selected status filter, AND organizer filter (UI filtering)
  const filteredStudents = students.filter((student) => {
    const matchesSearch =
      student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (student.tags &&
        student.tags.some((tag) =>
          tag.toLowerCase().includes(searchTerm.toLowerCase())
        ));

    const matchesStatusFilter =
      selectedFilter === "Tous" || student.status === selectedFilter;

    // Frontend organizer filter logic
    const matchesOrganizerFilter =
      !filterByOrganizer || // If filterByOrganizer is false, always match
      (currentAdminEmail && student.organizer === currentAdminEmail); // If true, match organizer email

    return matchesSearch && matchesStatusFilter && matchesOrganizerFilter;
  });



  if (loading) {
    return (
        <CoachingTrackSkeleton/>
    );
  }

  return (
    <div className="">
      {/* Header */}
      <Header
        PageName={"Coaching Tracking"}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
      />
      <div className="bg-slate-900 p-6 rounded-lg border border-slate-700">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              Student Management
            </h1>
            <p className="text-slate-400 mt-1">
              Track progress of your {stats.total} students
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:border-slate-600 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Students</p>
                <p className="text-3xl font-bold text-white">{stats.total}</p>
              </div>
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:border-slate-600 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Active Students</p>
                <p className="text-3xl font-bold text-white">{stats.active}</p>
              </div>
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:border-slate-600 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Average Progress</p>
                <p className="text-3xl font-bold text-white">
                  {stats.averageProgress}%
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:border-slate-600 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Alerts</p>
                <p className="text-3xl font-bold text-white">{stats.alerts}</p>
              </div>
              <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-slate-800 rounded-xl p-6 mb-6 border border-slate-700">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name, email or tag..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
              />
            </div>
            <div className="flex gap-2 items-center">
              <select
                value={selectedFilter}
                onChange={(e) => setSelectedFilter(e.target.value)}
                className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
              >
                <option value="Tous">All Status</option>
                <option value="Actif">Active</option>
                <option value="Inactif">Inactive</option>
              </select>

              {/* Organizer Filter Toggle */}
              {currentAdminEmail && ( // Only show filter if admin email is known
                <button
                  onClick={() => setFilterByOrganizer(!filterByOrganizer)}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors duration-200
                    ${filterByOrganizer
                      ? 'bg-purple-600 text-white hover:bg-purple-700'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                >
                  <Filter className="w-5 h-5" />
                  {filterByOrganizer ? 'My Students' : 'All Students'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Students List */}
        <div className="space-y-4">
          {filteredStudents.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <p>No students found matching your criteria</p>
            </div>
          ) : (
            filteredStudents.map((student) => (
              <div
                key={student.id}
                className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:border-slate-600 transition-all duration-200 hover:shadow-lg"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {/* Avatar */}
                    <div
                      className={`w-12 h-12 ${
                        student.bgColor
                      } rounded-full flex  items-center justify-center text-white font-bold text-lg`}
                    >
                      {student.profileImage ? (
                        <img className="rounded-full w-full h-full object-cover" src={student.profileImage} alt={student.name} />
                      ) : (
                        generateAvatar(student.name)
                      )}
                    </div>

                    {/* Student Info */}
                    <div>
                      <h3 className="text-start text-lg font-semibold text-white">
                        {student.name}
                      </h3>
                      <p className="text-slate-400 text-start text-sm">{student.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="bg-blue-600 text-blue-100 px-2 py-1 rounded text-xs font-medium">
                          {student.level}
                        </span>
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            student.status === "Actif"
                              ? "bg-green-600 text-green-100"
                              : "bg-red-600 text-red-100"
                          }`}
                        >
                          {student.status}
                        </span>
                        {student.organizer && ( // Display organizer if available
                            <span className="bg-indigo-600 text-indigo-100 px-2 py-1 rounded text-xs font-medium">
                                Organizer: {student.organizer}
                            </span>
                        )}
                        {student.tags &&
                          student.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="bg-slate-600 text-slate-100 px-2 py-1 rounded text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                      </div>
                    </div>
                  </div>

                  {/* Progress Stats */}
                  <div className="flex items-center space-x-8">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-white">
                        {student.progress}%
                      </p>
                      <p className="text-slate-400 text-sm">Progress</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-400">
                        {student.completed}
                      </p>
                      <p className="text-slate-400 text-sm">Completed</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-400">
                        {student.remaining}
                      </p>
                      <p className="text-slate-400 text-sm">Remaining</p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-2">
                      <Link to={`/admin/coaching-flow/${student.id}`}>
                        <button className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2">
                          <Eye className="w-4 h-4" />
                          View Roadmap
                        </button>
                      </Link>
                      <button className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors">
                        <MessageSquare className="w-5 h-5" />
                      </button>
                      <button className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors">
                        <Calendar className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default CoachingTrack;
